import React, { useState, useEffect, useCallback } from 'react';
import { FaSignature } from 'react-icons/fa';
import api from '../../../services/api';
import ThemedSelect from '../../common/ThemedSelect';
import PageHeader from '../../common/PageHeader';

const SignaturesRequired = () => {
  // Grouping-based state
  const [groupings, setGroupings] = useState([]);
  const [selectedGrouping, setSelectedGrouping] = useState('');

  // Common state
  const [signatures, setSignatures] = useState([]);
  const [uniqueSigners, setUniqueSigners] = useState([]); // For autocomplete { name, surname, company }

  // Form state
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [signerSuggestions, setSignerSuggestions] = useState([]); // Filtered suggestions for dropdown
  const [showSuggestions, setShowSuggestions] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch groupings
  const fetchGroupings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.routeSummary.getGroupings();
      setGroupings(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching groupings:', err);
      setError('Failed to load groupings.');
      setGroupings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch signatures for the selected grouping
  const fetchGroupingSignatures = useCallback(async (groupingId) => {
    if (!groupingId) {
      setSignatures([]);
      return;
    }
    try {
      setLoading(true);
      const response = await api.signatures.getGroupingSignatures(groupingId);
      setSignatures(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching grouping signatures:', err);
      setError('Failed to load signatures for the selected grouping.');
      setSignatures([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch unique signer details for autocomplete
  const fetchUniqueSigners = useCallback(async () => {
    try {
      setLoading(true);
      // Use getAllUniqueSigners to get both location and grouping signers
      const response = await api.signatures.getAllUniqueSigners();
      setUniqueSigners(response.data || []); // Ensure it's an array
      setError(null);
    } catch (err) {
      console.error('Error fetching unique signers:', err);
      setError('Failed to load signer details.');
      setUniqueSigners([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchGroupings();
    fetchUniqueSigners();
  }, [fetchGroupings, fetchUniqueSigners]);

  // Fetch signatures when grouping is selected
  useEffect(() => {
    if (selectedGrouping) {
      fetchGroupingSignatures(selectedGrouping);
    } else {
      setSignatures([]);
    }
  }, [selectedGrouping, fetchGroupingSignatures]);

  // Handle form submission
  const handleAddSignature = async (e) => {
    e.preventDefault();

    if (!selectedGrouping) {
      setError('Please select a grouping.');
      return;
    }

    if (!name || !title) {
      setError('Please fill in all signer details.');
      return;
    }

    try {
      setLoading(true);
      const newSignature = {
        groupingId: selectedGrouping,
        name: name,
        title: title,
      };
      const response = await api.signatures.createGroupingSignature(newSignature);

      // Ensure response.data exists before adding
      if (response.data) {
        setSignatures([...signatures, response.data]); // Add to current list
      } else {
        console.error('Create signature response did not contain data:', response);
        setError('Failed to add signature: Invalid response from server.');
        setLoading(false); // Ensure loading is stopped
        return; // Stop execution here
      }

      // Refetch unique signers if the added one is new
      const addedSignerKey = `${name}-${title}`;
      if (!uniqueSigners.some((s) => `${s.name}-${s.title}` === addedSignerKey)) {
        fetchUniqueSigners();
      }
      // Reset form
      setName('');
      setTitle('');
      setShowSuggestions(false);
      setError(null);
    } catch (err) {
      console.error('Error adding signature:', err);
      setError('Failed to add signature.');
    } finally {
      setLoading(false);
    }
  };

  // Autocomplete logic for signer details
  const handleSignerInputChange = (e) => {
    const { name: fieldName, value } = e.target;
    if (fieldName === 'name') setName(value);
    if (fieldName === 'title') setTitle(value);

    const searchTerm = `${name} ${title}`.toLowerCase().trim();
    if (searchTerm.length > 0) {
      const filtered = uniqueSigners.filter((signer) =>
        `${signer.name} ${signer.title}`.toLowerCase().includes(searchTerm)
      );
      setSignerSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setSignerSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (signer) => {
    setName(signer.name);
    setTitle(signer.title);
    setSignerSuggestions([]);
    setShowSuggestions(false);
  };

  // Handle signature removal
  const handleRemoveSignature = async (signatureId) => {
    if (!signatureId) return;

    // Optional: Add confirmation dialog
    if (!window.confirm('Are you sure you want to remove this signature?')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await api.signatures.deleteGroupingSignature(signatureId);

      // Update state by removing the signature
      setSignatures((prevSignatures) => prevSignatures.filter((sig) => sig.id !== signatureId));
    } catch (err) {
      console.error('Error removing signature:', err);
      setError('Failed to remove signature.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <PageHeader
        icon={FaSignature}
        title="Signatures Required"
        subtitle="Manage required signatures for route groupings"
      />

      <div className="max-w-[1800px] mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6 space-y-6">
        {error && (
          <div
            className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4"
            role="alert"
          >
            {error}
          </div>
        )}
        {loading && <div className="text-center my-4">Loading...</div>}

        {/* Grouping Selection */}
        <div className="mb-6 bg-surface p-4 rounded shadow">
          <label htmlFor="grouping" className="block text-sm font-medium text-gray-700 mb-1">
            Select Grouping
          </label>
          <ThemedSelect
            id="grouping"
            value={selectedGrouping}
            onChange={(e) => setSelectedGrouping(e.target.value)}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md shadow-sm"
            disabled={loading}
          >
            <option value="">Select Grouping...</option>
            {groupings.map((grouping) => (
              <option key={grouping.id} value={grouping.id}>
                {grouping.name}
              </option>
            ))}
          </ThemedSelect>
        </div>

        {/* Display Signatures */}
        {selectedGrouping && (
          <div className="mb-6 bg-surface p-4 rounded shadow">
            <h2 className="text-lg font-medium text-gray-900 mb-3">
              Signatures for{' '}
              {groupings.find((g) => g.id === parseInt(selectedGrouping))?.name ||
                'Selected Grouping'}
            </h2>
            {signatures.length > 0 ? (
              <ul className="divide-y divide-gray-200">
                {signatures.map((sig) => (
                  <li key={sig.id} className="py-3 flex justify-between items-center">
                    <span>
                      {sig.name} - {sig.title}
                    </span>
                    <button
                      onClick={() => handleRemoveSignature(sig.id)}
                      className="ml-4 text-sm font-medium text-red-600 hover:text-red-800 disabled:opacity-50"
                      disabled={loading}
                      aria-label={`Remove signature for ${sig.name}`}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500">{loading ? 'Loading...' : 'No signatures found.'}</p>
            )}
          </div>
        )}

        {/* Add New Signature Form */}
        {selectedGrouping && (
          <div className="bg-surface p-4 rounded shadow">
            <h2 className="text-lg font-medium text-gray-900 mb-3">Add New Signature</h2>
            <form onSubmit={handleAddSignature} className="space-y-4">
              <div className="relative">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={name}
                  onChange={handleSignerInputChange}
                  onFocus={() => setShowSuggestions(true)} // Show suggestions on focus too
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  required
                  autoComplete="off"
                />
              </div>
              <div className="relative">
                <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                  Company Name
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={title}
                  onChange={handleSignerInputChange}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 150)} // Hide suggestions on blur with delay for click
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  required
                  autoComplete="off"
                />
                {/* Autocomplete Suggestions Dropdown */}
                {showSuggestions && signerSuggestions.length > 0 && (
                  <ul className="absolute z-10 w-full bg-surface border border-gray-300 rounded-md shadow-lg mt-1 max-h-60 overflow-auto">
                    {signerSuggestions.map((signer, index) => (
                      <li
                        key={`${signer.name}-${signer.title}-${index}`} // Ensure unique key
                        className="px-3 py-2 cursor-pointer hover:bg-gray-100"
                        onMouseDown={(e) => {
                          // Use onMouseDown to prevent blur before click registers
                          e.preventDefault();
                          handleSuggestionClick(signer);
                        }}
                      >
                        {signer.name} - {signer.title}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <button
                type="submit"
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                disabled={loading || !selectedGrouping}
              >
                {loading ? 'Adding...' : 'Add Signature'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default SignaturesRequired;
